import React, { useEffect, useState } from 'react';
import { Save, Building2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import apiClient from '../../utils/api';

export const CompanyAdminProfile: React.FC = () => {
  const { user } = useAuth();
  const [company, setCompany] = useState<any>(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', country: '', webhookUrl: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user?.companyId) return;
    apiClient.getCompany(user.companyId).then(res => {
      const c = res.data.data;
      setCompany(c);
      setForm({
        name: c.name ?? '',
        email: c.email ?? '',
        phone: c.phone ?? '',
        address: c.address ?? '',
        city: c.city ?? '',
        country: c.country ?? '',
        webhookUrl: c.webhookUrl ?? '',
      });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await apiClient.updateCompany(user!.companyId!, form);
      setSuccess('Company profile updated successfully.');
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to update company');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Company Profile</h1>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="stat-card" style={{ borderLeft: '4px solid #3b82f6' }}>
          <div className="stat-label">Status</div>
          <div><span className={`badge badge-${company?.status?.toLowerCase()}`}>{company?.status}</span></div>
        </div>
        <div className="stat-card" style={{ borderLeft: '4px solid #22c55e' }}>
          <div className="stat-label">Currency</div>
          <div className="stat-value" style={{ color: '#22c55e', fontSize: 20 }}>{company?.currency}</div>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <Building2 size={20} color="#3b82f6" />
          <h2 style={{ fontSize: 15, fontWeight: 600 }}>Edit Company Information</h2>
        </div>

        {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{success}</div>}
        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}

        <form onSubmit={submit}>
          <div className="form-grid">
            <div className="form-group full"><label>Company Name *</label><input required value={form.name} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label>Email</label><input type="email" value={form.email} onChange={e => set('email', e.target.value)} /></div>
            <div className="form-group"><label>Phone</label><input value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
            <div className="form-group full"><label>Address</label><input value={form.address} onChange={e => set('address', e.target.value)} /></div>
            <div className="form-group"><label>City</label><input value={form.city} onChange={e => set('city', e.target.value)} /></div>
            <div className="form-group"><label>Country</label><input value={form.country} onChange={e => set('country', e.target.value)} /></div>
            <div className="form-group full"><label>Webhook URL</label><input type="url" placeholder="https://your-system.com/webhook" value={form.webhookUrl} onChange={e => set('webhookUrl', e.target.value)} /></div>
          </div>
          <div style={{ marginTop: 20 }}>
            <button type="submit" className="btn btn-primary" disabled={saving}><Save size={15} /> {saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>

        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
          <h3 style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 12 }}>Integrations</h3>
          <div style={{ display: 'flex', gap: 16 }}>
            <div style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>API Integration: </span>
              <span style={{ fontWeight: 600, color: company?.allowApiIntegration ? '#16a34a' : '#dc2626' }}>
                {company?.allowApiIntegration ? 'Enabled' : 'Disabled'}
              </span>
            </div>
            <div style={{ padding: '10px 16px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }}>
              <span style={{ color: '#64748b' }}>Webhooks: </span>
              <span style={{ fontWeight: 600, color: company?.allowWebhooks ? '#16a34a' : '#dc2626' }}>
                {company?.allowWebhooks ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>Contact your Super Admin to enable/disable integrations.</p>
        </div>
      </div>
    </>
  );
};
